/**
 * Director API functions
 */
import apiService from "~/services/service";
import API_ROUTES_V2 from "~/api-routes-v2";

export interface DirectorAuditEntry {
  id: string;
  original_pk: string;
  action: "create" | "update" | "delete";
  entities: string[];
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  actor_name: string;
  actor_email: string;
  actor_workos_id: string;
  timestamp: string;
  name: string | null;
  tenant_id: string;
  createdAt: string;
  updatedAt: string | Date;
  object_name?: string | null;
}

export interface DirectorAuditApiResponse {
  success: boolean;
  message: string;
  data: DirectorAuditEntry[];
  statusCode: number;
  status: boolean;
}

export interface DirectorAuditResponse {
  success: boolean;
  message: string;
  data: DirectorAuditEntry[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Fetch director address audit logs with pagination
 */
export async function getAddressAudit(
  addressId: string,
  page = 1,
  pageSize = 20,
): Promise<DirectorAuditResponse> {
  // Build URL - the API route is /addresses/{id}/audit?page={page}&pageSize={pageSize}
  // We need to construct it manually to avoid template replacement issues
  const baseUrl = API_ROUTES_V2.FETCH_ADDRESS_AUDIT.replace("{id}", addressId);
  const url = `${baseUrl.replace(
    "?page={page}&objectType={objectType}",
    "",
  )}?page=${page}&pageSize=${pageSize}`;

  const response = await apiService<DirectorAuditApiResponse>(url, "GET", {
    skipCache: true,
  });

  // Extract the data array directly from response
  const auditEntries = response.data || [];

  // Calculate pagination based on the response
  const total = auditEntries.length;
  const pagination = {
    page,
    pageSize,
    total,
    totalPages: Math.ceil(total / pageSize),
  };

  // Normalize the response data structure for StepCard compatibility
  const normalizedData = auditEntries.map((item) => {
    // Parse the ISO timestamp string to a Date object
    let parsedTimestamp: Date | string = item.timestamp;
    try {
      const parsedDate = new Date(item.timestamp);
      if (!Number.isNaN(parsedDate.getTime())) {
        parsedTimestamp = parsedDate;
      } else {
        console.warn(
          "Failed to parse timestamp, using original:",
          item.timestamp,
        );
      }
    } catch (error) {
      console.warn("Failed to parse timestamp:", item.timestamp, error);
    }

    const normalizedOldData: Record<string, unknown> | null =
      item.old_data && Object.keys(item.old_data).length > 0
        ? item.old_data
        : null;
    const normalizedNewData: Record<string, unknown> | null =
      item.new_data && Object.keys(item.new_data).length > 0
        ? item.new_data
        : null;

    return {
      ...item,
      old_data: normalizedOldData,
      new_data: normalizedNewData,
      name: item.object_name || null,
      updatedAt: parsedTimestamp,
    };
  });

  return {
    success: response.success,
    message: response.message,
    data: normalizedData,
    pagination,
  };
}

/**
 * Fetch director audit logs with pagination
 * Uses the endpoint: /director/{id}/audit?page={page}&tab={tab}
 */
export async function getDirectorAudit(
  directorId: string,
  page = 1,
  pageSize = 20,
  tab?: string,
): Promise<DirectorAuditResponse> {
  // Build URL using the correct endpoint: /director/{id}/audit?page={page}&tab={tab}
  const url = API_ROUTES_V2.FETCH_DIRECTOR_AUDIT.replace("{id}", directorId);
  let finalUrl = `${url.replace("?page={page}&tab={tab}", "")}?page=${page}`;
  if (tab) {
    finalUrl += `&tab=${tab}`;
  }

  const response = await apiService<DirectorAuditApiResponse>(finalUrl, "GET");

  // Extract the data array directly from response
  const auditEntries = response.data || [];

  // Calculate pagination based on the response
  // Since the API doesn't return pagination info, we'll estimate it
  const total = auditEntries.length;
  const pagination = {
    page,
    pageSize,
    total,
    totalPages: Math.ceil(total / pageSize),
  };

  // Normalize the response data structure for StepCard compatibility
  const normalizedData = auditEntries.map((item) => {
    // Parse the ISO timestamp string to a Date object
    let parsedTimestamp: Date | string = item.timestamp;
    try {
      // The API returns ISO format: "2026-02-05T14:27:35.406Z"
      const parsedDate = new Date(item.timestamp);
      // Check if the date is valid
      if (!Number.isNaN(parsedDate.getTime())) {
        parsedTimestamp = parsedDate;
      } else {
        console.warn(
          "Failed to parse timestamp, using original:",
          item.timestamp,
        );
      }
    } catch (error) {
      console.warn("Failed to parse timestamp:", item.timestamp, error);
    }

    // Use old_data and new_data directly from API response
    // They are already objects with field keys as properties
    const normalizedOldData: Record<string, unknown> | null =
      item.old_data && Object.keys(item.old_data).length > 0
        ? item.old_data
        : null;
    const normalizedNewData: Record<string, unknown> | null =
      item.new_data && Object.keys(item.new_data).length > 0
        ? item.new_data
        : null;

    return {
      ...item,
      old_data: normalizedOldData,
      new_data: normalizedNewData,
      name: item.object_name || null,
      updatedAt: parsedTimestamp, // Map timestamp to updatedAt for StepCard
    };
  });

  return {
    success: response.success,
    message: response.message,
    data: normalizedData,
    pagination,
  };
}
